import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { useSwipeable } from 'react-swipeable'

interface UseSwipeDismissOptions {
  isActive: boolean
  onDismiss: () => void
  /** Fraction of element height to trigger dismiss (default 0.3) */
  threshold?: number
}

interface UseSwipeDismissReturn {
  swipeRef: RefObject<HTMLDivElement | null>
  swipeHandlers: ReturnType<typeof useSwipeable>
  swipeStyle: React.CSSProperties
  /** 0–1, how "open" the sheet still is. Use on backdrop opacity. */
  backdropOpacity: number
}

export function useSwipeDismiss({
  isActive,
  onDismiss,
  threshold = 0.3,
}: UseSwipeDismissOptions): UseSwipeDismissReturn {
  const ref = useRef<HTMLDivElement>(null)
  const [translateY, setTranslateY] = useState(0)
  const [transitioning, setTransitioning] = useState(false)
  const isDragging = useRef(false)

  // Reset when sheet opens/closes
  useEffect(() => {
    if (!isActive) {
      setTranslateY(0)
      setTransitioning(false)
      isDragging.current = false
    }
  }, [isActive])

  const snapBack = useCallback(() => {
    setTransitioning(true)
    setTranslateY(0)
    setTimeout(() => setTransitioning(false), 200)
  }, [])

  // A non-downward swipe ends the drag: spring back to the resting position.
  const abortDrag = () => {
    if (isDragging.current) {
      isDragging.current = false
      snapBack()
    }
  }

  const dismissOrSnapBack = (deltaY: number) => {
    const elHeight = ref.current?.offsetHeight ?? 300

    if (deltaY > elHeight * threshold) {
      // Dismiss: animate to full height then call onDismiss
      setTransitioning(true)
      setTranslateY(elHeight)
      setTimeout(() => {
        onDismiss()
        setTranslateY(0)
        setTransitioning(false)
      }, 200)
    } else {
      snapBack()
    }
  }

  const handlers = useSwipeable({
    onSwiping(e) {
      if (transitioning) return
      // Only allow downward swipe (positive deltaY)
      if (e.deltaY <= 0) return
      isDragging.current = true
      setTranslateY(Math.max(0, e.deltaY))
    },
    onSwipedDown(e) {
      if (!isDragging.current) return
      isDragging.current = false

      // No movement (just a tap) — skip snap-back to avoid replaying entry animation
      if (translateY === 0) return

      // Use e.deltaY from the event — no stale state risk
      dismissOrSnapBack(e.deltaY)
    },
    // No onSwiped — it fires BEFORE directional handlers, which would
    // snap back before onSwipedDown could process the dismiss.
    onSwipedUp: abortDrag,
    onSwipedLeft: abortDrag,
    onSwipedRight: abortDrag,
    trackMouse: false,
    trackTouch: true,
    preventScrollOnSwipe: false,
    delta: 10,
  })

  const elHeight = ref.current?.offsetHeight ?? 1
  const backdropOpacity = Math.max(0, Math.min(1, 1 - translateY / elHeight))

  const swipeStyle: React.CSSProperties =
    translateY > 0 || transitioning
      ? {
          transform: `translateY(${translateY}px)`,
          transition: isDragging.current && !transitioning ? 'none' : 'transform 0.2s ease-out',
          animation: 'none',
        }
      : {}

  return {
    swipeRef: ref,
    swipeHandlers: handlers,
    swipeStyle,
    backdropOpacity,
  }
}
